import type { SupabaseClient } from "@supabase/supabase-js";

export const CREATOR_CATEGORIES = ["youtube", "blog", "vibe", "book"] as const;
export type CreatorCategory = typeof CREATOR_CATEGORIES[number];
export const CREATOR_INFO = {
  youtube: { label: "교사들의 유튜브", route: "/informate/youtubers", action: "채널 만나보기", table: "informate_youtuber_list_view", fields: "id,name,introduction,thumbnail_image,youtube_id" },
  blog: { label: "교사들의 블로그", route: "/informate/blogers", action: "블로그 둘러보기", table: "informate_bloger_list_view", fields: "id,name,introduction,thumbnail_image,naver_id" },
  vibe: { label: "교사들의 바이브코딩", route: "/informate/vibe-sites", action: "사이트 사용해 보기", table: "informate_vibe_site_list_view", fields: "id,title,description,preview_image_url,url,author_display_name" },
  book: { label: "교사들의 책", route: "/informate/books", action: "책 자세히 보기", table: "books_book", fields: "id,title,description,image,author" },
} as const;

export type CreatorSpotlight = {
  id: string;
  category: CreatorCategory;
  title: string;
  description: string;
  byline: string;
  imagePath: string | null;
  href: string;
};

type Row = Record<string, unknown>;
const value = (row: Row, key: string) => typeof row[key] === "string" ? (row[key] as string).trim() : "";

export function safeHttpUrl(raw: string): string | null {
  try {
    const url = new URL(raw);
    return ["http:", "https:"].includes(url.protocol) && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

/** Only channel/profile roots are accepted, never individual videos or posts. */
export function creatorHomeUrl(category: "youtube" | "blog", raw: string): string | null {
  let id = raw.trim();
  if (/^https?:\/\//i.test(id)) {
    const safe = safeHttpUrl(id);
    if (!safe) return null;
    const url = new URL(safe);
    const parts = url.pathname.split("/").filter(Boolean);
    if (category === "blog") {
      if (!["blog.naver.com", "m.blog.naver.com"].includes(url.hostname)) return null;
      id = /^(PostView|PostList)\.naver$/i.test(parts[0] || "") ? url.searchParams.get("blogId") || "" : parts[0] || "";
    } else {
      if (!["youtube.com", "www.youtube.com", "m.youtube.com"].includes(url.hostname)) return null;
      if (parts[0]?.startsWith("@")) id = decodeURIComponent(parts[0]);
      else if (["channel", "c", "user"].includes(parts[0]) && parts[1]) {
        return `https://www.youtube.com/${parts[0]}/${encodeURIComponent(decodeURIComponent(parts[1]))}`;
      } else return null;
    }
  }
  if (category === "blog") return /^[a-zA-Z0-9_-]+$/.test(id) ? `https://blog.naver.com/${encodeURIComponent(id)}` : null;
  if (/^@[^\s/?#]+$/u.test(id)) return `https://www.youtube.com/${encodeURIComponent(id).replace(/^%40/, "@")}`;
  return /^UC[a-zA-Z0-9_-]+$/.test(id) ? `https://www.youtube.com/channel/${id}` : null;
}

export function toCreatorSpotlight(category: CreatorCategory, row: Row): CreatorSpotlight | null {
  const title = value(row, category === "youtube" || category === "blog" ? "name" : "title");
  const id = String(row.id ?? "");
  if (!title || !/^\d+$/.test(id)) return null;
  const href = category === "youtube" ? creatorHomeUrl(category, value(row, "youtube_id"))
    : category === "blog" ? creatorHomeUrl(category, value(row, "naver_id"))
    : category === "vibe" ? safeHttpUrl(value(row, "url")) : `/informate/books/${id}`;
  if (!href) return null;
  return {
    id: `${category}:${id}`, category, title, href,
    description: value(row, category === "youtube" || category === "blog" ? "introduction" : "description"),
    byline: value(row, category === "book" ? "author" : "author_display_name"),
    imagePath: value(row, category === "book" ? "image" : category === "vibe" ? "preview_image_url" : "thumbnail_image") || null,
  };
}

/** Equal chance per available category, then per entry; no first-page/popularity bias. */
export async function fetchCreatorSpotlight(client: SupabaseClient | null, signal?: AbortSignal, random = Math.random): Promise<CreatorSpotlight | null> {
  if (!client) return null;
  const query = (category: CreatorCategory, head: boolean) => {
    const info = CREATOR_INFO[category];
    let q = client.from(info.table).select(head ? "id" : info.fields, head ? { count: "exact", head: true } : {});
    if (category === "book") q = q.eq("status", "approved");
    if (category === "vibe") q = q.eq("status", "published");
    return signal ? q.abortSignal(signal) : q;
  };
  const counts = await Promise.all(CREATOR_CATEGORIES.map(async category => {
    try {
      const { count, error } = await query(category, true);
      return { category, count: error ? 0 : count || 0 };
    } catch { return { category, count: 0 }; }
  }));
  const available = counts.filter(({ count }) => count > 0);
  while (available.length && !signal?.aborted) {
    const [{ category, count }] = available.splice(Math.floor(random() * available.length), 1);
    const offset = Math.floor(random() * count);
    // Try neighbouring rows too if a creator has an invalid link or was just deleted.
    for (let attempt = 0; attempt < Math.min(count, 3) && !signal?.aborted; attempt++) {
      const index = (offset + attempt) % count;
      try {
        const { data, error } = await query(category, false).order("id", { ascending: true }).range(index, index);
        if (!error && data?.[0]) {
          const pick = toCreatorSpotlight(category, data[0] as unknown as Row);
          if (pick) return pick;
        }
      } catch { /* Try another public entry/category. */ }
    }
  }
  return null;
}
